import { useMemo } from 'react'
import SortableTh from '../Shared/SortableTh'
import usePersistedState from '../../hooks/usePersistedState'
import { nextSort } from '../../utils/sort'
import {
  DEFAULT_INCIDENT_SORT,
  INCIDENT_SEVERITY_META,
  INCIDENT_TABLE_COLUMNS,
  isIncidentOverdue,
  isIncidentSortable,
  severityColor,
  sortIncidents,
  statusColor,
} from '../../utils/incidentTable'
import '../../styles/data-table.css'

/** Formatea una fecha ISO como `dd/mm/aa` (o `—`). */
const formatDate = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('es-CR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
}

/** Etiqueta coloreada compacta. El tono lo usa .gm-tono según claro/oscuro. */
const Badge = ({ color, icon, children }) => (
  <span className="tt-badge gm-tono" style={{ '--tono': color }}>
    {icon && <i className={`fa-solid ${icon}`} />}
    {children}
  </span>
)

/** Cómo se pinta cada columna de `INCIDENT_TABLE_COLUMNS`. */
const CELL_RENDERERS = {
  code: (i) => (
    <>
      <span className="tt-code">{i.Code}</span>
      {i.PersonalDataAffected && (
        <i
          className="fa-solid fa-user-shield tt-flag text-warning"
          title="Datos personales afectados"
        />
      )}
      {isIncidentOverdue(i) && (
        <i
          className="fa-regular fa-clock tt-flag text-danger"
          title="Fuera del plazo de respuesta"
        />
      )}
    </>
  ),
  severity: (i) => (
    <Badge
      color={severityColor(i.Severity)}
      icon={INCIDENT_SEVERITY_META[i.Severity]?.icon}
    >
      {i.Severity}
    </Badge>
  ),
  status: (i) => <Badge color={statusColor(i.Status)}>{i.Status}</Badge>,
  description: (i) => (
    <span className="tt-desc" title={i.Description}>
      {i.Description}
    </span>
  ),
  detectedAt: (i) => (
    <span className="tt-muted">{formatDate(i.DetectedAt)}</span>
  ),
  reportedBy: (i) => <span className="tt-muted">{i.ReportedBy || '—'}</span>,
  assets: (i) => (
    <span className="tt-muted">
      <i className="fa-solid fa-cubes me-1" />
      {i.assets?.length || 0}
    </span>
  ),
}

/**
 * Tabla compacta de la bitácora de incidentes: orden por columna (persistido)
 * y acciones por fila (avanzar estado, editar, eliminar). Presentacional: los
 * datos llegan filtrados y las acciones se delegan a la página.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.incidents
 * @param {boolean} props.loading
 * @param {number} props.total - Total sin filtros (para el conteo).
 * @param {boolean} props.hasFilters - Si hay filtros activos (cambia el vacío).
 * @param {(incident: Object) => void} props.onOpen
 * @param {(incident: Object) => void} props.onEdit
 * @param {(incident: Object, status: string) => void} props.onChangeStatus
 * @param {(incident: Object) => void} props.onDelete
 * @param {() => void} props.onCreate
 * @returns {JSX.Element}
 */
const IncidentTable = ({
  incidents,
  loading,
  total,
  hasFilters,
  onOpen,
  onEdit,
  onChangeStatus,
  onDelete,
  onCreate,
}) => {
  const [sort, setSort] = usePersistedState(
    'incidents.sort',
    DEFAULT_INCIDENT_SORT
  )
  const rows = useMemo(() => sortIncidents(incidents, sort), [incidents, sort])
  const colSpan = INCIDENT_TABLE_COLUMNS.length + 1

  /** Evita que el clic en una acción abra el detalle de la fila. */
  const act = (fn) => (e) => {
    e.stopPropagation()
    fn()
  }

  return (
    <div className="tt">
      <div className="tt-bar">
        <span className="tt-muted">
          <strong className="tt-strong">{rows.length}</strong>
          {hasFilters && ` de ${total}`} incidentes
        </span>
        {loading && (
          <span className="spinner-border spinner-border-sm text-primary" />
        )}
      </div>

      <div className="tt-scroll">
        <table className="tt-table">
          <thead>
            <tr>
              {INCIDENT_TABLE_COLUMNS.map((col) => (
                <SortableTh
                  key={col.key}
                  label={col.label}
                  className={col.className}
                  sortable={isIncidentSortable(col.key)}
                  direction={sort?.key === col.key ? sort.dir : null}
                  onSort={() => setSort((s) => nextSort(s, col.key))}
                />
              ))}
              <th aria-label="Acciones" />
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="tt-empty">
                  {loading ? (
                    'Cargando incidentes…'
                  ) : hasFilters ? (
                    'No hay incidentes que coincidan con los filtros.'
                  ) : (
                    <>
                      <i className="fa-solid fa-shield-halved d-block fs-4 mb-2 opacity-50" />
                      No hay incidentes registrados.{' '}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 align-baseline"
                        onClick={onCreate}
                      >
                        Registrar el primero
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              rows.map((incident) => (
                <tr
                  key={incident.Id}
                  className="tt-row"
                  onClick={() => onOpen(incident)}
                >
                  {INCIDENT_TABLE_COLUMNS.map((col) => (
                    <td key={col.key} className={col.className || ''}>
                      {CELL_RENDERERS[col.key](incident)}
                    </td>
                  ))}
                  <td>
                    <div className="tt-actions">
                      {incident.Status === 'Abierto' && (
                        <button
                          type="button"
                          className="tt-icon-btn"
                          title="Marcar en curso"
                          onClick={act(() =>
                            onChangeStatus(incident, 'En curso')
                          )}
                        >
                          <i className="fa-solid fa-play" />
                        </button>
                      )}
                      {incident.Status !== 'Cerrado' ? (
                        <button
                          type="button"
                          className="tt-icon-btn"
                          title="Cerrar incidente"
                          onClick={act(() =>
                            onChangeStatus(incident, 'Cerrado')
                          )}
                        >
                          <i className="fa-solid fa-circle-check" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="tt-icon-btn"
                          title="Reabrir incidente"
                          onClick={act(() =>
                            onChangeStatus(incident, 'En curso')
                          )}
                        >
                          <i className="fa-solid fa-rotate-left" />
                        </button>
                      )}
                      <button
                        type="button"
                        className="tt-icon-btn"
                        title="Editar"
                        onClick={act(() => onEdit(incident))}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        type="button"
                        className="tt-icon-btn is-danger"
                        title="Eliminar"
                        onClick={act(() => onDelete(incident))}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default IncidentTable
